// Browser client for the VAN voice relay, mirroring iOS WebRTCService.
//
// Flow: POST /van/start -> session_id + ice_servers, open wss://.../van/ws/<id>,
// exchange SDP/ICE over that socket, then drive turns with van_listen/van_speak.
// The mic track is added up front but stays disabled until the user starts a turn,
// exactly as the native client does.

const PING_INTERVAL_MS = 10_000
// Last audio RTP packets travel over UDP and land after the WebSocket message, so
// van_speak waits for the pipeline to drain or the relay answers a turn behind.
const AUDIO_FLUSH_MS = 350
// end_session handshake: the relay answers session_ended once the summary job is saved.
const END_SESSION_ACK_MS = 10_000
const MAX_END_SESSION_ATTEMPTS = 3
const END_SESSION_RETRY_MS = 1_000
// VAN's turn ends when its audio has started and then gone quiet, matching the
// native client's 100ms poll x 15 silent polls.
const LEVEL_POLL_MS = 100
const SILENCE_POLLS_TO_END_TURN = 15
const SPEAKING_RMS = 0.01
// Nothing at all came back: drop the turn rather than stranding the button.
const RESPONSE_TIMEOUT_MS = 15_000

export const VAN_STATUS = {
  IDLE: "idle",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  LISTENING: "listening",
  THINKING: "thinking",
  CLOSED: "closed",
  ERROR: "error"
}

export default class VanSession {
  constructor({ relayBase, onStatus, onError, onSessionEnded, onVanSpeaking }) {
    this.relayBase = relayBase.replace(/\/$/, "")
    this.onStatus = onStatus ?? (() => {})
    this.onError = onError ?? (() => {})
    this.onSessionEnded = onSessionEnded ?? (() => {})
    this.onVanSpeaking = onVanSpeaking ?? (() => {})

    this.ws = null
    this.pc = null
    this.micStream = null
    this.micTrack = null
    this.remoteAudio = null
    this.pingTimer = null
    this.flushTimer = null
    this.isClosed = false
    this.hasStartedTurn = false
    this.resolveEndSession = null

    this.levelTimer = null
    this.responseTimer = null
    this.vanStartedSpeaking = false
    this.silentPolls = 0
  }

  // WebSocket has no relative-URL form, so an empty relay base (the dev/preview
  // proxy path) has to be resolved against the page origin.
  get wsBase() {
    if (this.relayBase) return this.relayBase.replace(/^http/, "ws")
    const { protocol, host } = window.location
    return `${protocol === "https:" ? "wss" : "ws"}://${host}`
  }

  async start({ restaurantId, itemId, vanMode, oldMemory }) {
    this.onStatus(VAN_STATUS.CONNECTING)

    const body = { restaurant_id: restaurantId, item_id: itemId }
    if (vanMode) body.van_mode = vanMode
    if (oldMemory) body.old_memory = oldMemory

    const response = await fetch(`${this.relayBase}/van/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`VAN start failed (${response.status})`)

    const { session_id: sessionId, ice_servers: iceServers } = await response.json()
    if (!sessionId) throw new Error("VAN start returned no session_id")

    this.sessionId = sessionId
    // The sheet closed while van/start was in flight
    if (this.isClosed) return sessionId
    // The relay hands back Cloudflare TURN alongside STUN — needed on networks that
    // block direct UDP, which a hotel guest network frequently does.
    await this.openSocket(sessionId, iceServers ?? [{ urls: "stun:stun.l.google.com:19302" }])
    return sessionId
  }

  openSocket(sessionId, iceServers) {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${this.wsBase}/van/ws/${sessionId}`)
      this.ws = ws

      ws.onopen = async () => {
        // The sheet closed while the socket was opening
        if (this.isClosed) {
          ws.close()
          resolve()
          return
        }
        this.pingTimer = setInterval(() => this.send({ type: "ping" }), PING_INTERVAL_MS)
        try {
          await this.openPeerConnection(iceServers)
          resolve()
        } catch (error) {
          reject(error)
        }
      }
      ws.onmessage = (event) => this.handleMessage(event.data)
      ws.onerror = () => this.fail("Lost connection to VAN")
      ws.onclose = () => {
        this.resolveEndSession?.(false)
        if (!this.isClosed) this.onStatus(VAN_STATUS.CLOSED)
      }
    })
  }

  async openPeerConnection(iceServers) {
    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    })
    // The sheet closed while the mic prompt was open
    if (this.isClosed) {
      this.micStream.getTracks().forEach((track) => track.stop())
      return
    }

    const pc = new RTCPeerConnection({ iceServers })
    this.pc = pc

    this.remoteAudio = new Audio()
    this.remoteAudio.autoplay = true
    pc.ontrack = (event) => {
      this.remoteAudio.srcObject = event.streams[0]
      this.remoteTrack = event.track
      this.watchRemoteLevel(event.streams[0])
    }

    pc.onicecandidate = (event) => {
      if (!event.candidate) return
      this.send({
        type: "client_ice",
        payload: {
          candidate: event.candidate.candidate,
          sdpMid: event.candidate.sdpMid ?? "",
          sdpMLineIndex: event.candidate.sdpMLineIndex ?? 0
        }
      })
    }

    this.micTrack = this.micStream.getAudioTracks()[0]
    this.micTrack.enabled = false
    pc.addTrack(this.micTrack, this.micStream)

    const offer = await pc.createOffer({ offerToReceiveAudio: true })
    await pc.setLocalDescription(offer)
    // The sheet closed while the offer was being prepared: never hand it to the relay,
    // or VAN answers and starts speaking into a screen nobody is looking at.
    if (this.isClosed) {
      this.teardownMedia()
      this.closeConnections()
      return
    }
    this.send({ type: "client_offer", payload: { sdp: offer.sdp, type: "offer" } })
  }

  async handleMessage(raw) {
    if (this.isClosed) return
    let message
    try {
      message = JSON.parse(raw)
    } catch {
      return
    }

    if (message.type === "server_answer" && message.payload?.sdp) {
      await this.pc.setRemoteDescription({ type: "answer", sdp: message.payload.sdp })
      if (this.micTrack) this.micTrack.enabled = false
      this.onStatus(VAN_STATUS.CONNECTED)
      return
    }

    if (message.type === "server_ice" && message.payload?.candidate) {
      try {
        await this.pc.addIceCandidate({
          candidate: message.payload.candidate,
          sdpMid: message.payload.sdpMid ?? "",
          sdpMLineIndex: message.payload.sdpMLineIndex ?? 0
        })
      } catch {
        // A candidate arriving before the remote description is expected; ignore.
      }
      return
    }

    if (message.type === "session_ended") {
      this.resolveEndSession?.(true)
      return
    }

    if (message.type === "session_end_error") {
      this.resolveEndSession?.(false)
    }
  }

  // A turn starts: silence anything left in the jitter buffer from the previous
  // answer, then open the mic.
  startTurn() {
    this.hasStartedTurn = true
    clearTimeout(this.responseTimer)
    this.vanStartedSpeaking = false
    this.silentPolls = 0
    if (this.remoteTrack) this.remoteTrack.enabled = false
    if (this.micTrack) this.micTrack.enabled = true
    this.send({ type: "van_listen" })
    this.onStatus(VAN_STATUS.LISTENING)
  }

  endTurn() {
    if (this.micTrack) this.micTrack.enabled = false
    if (this.remoteTrack) this.remoteTrack.enabled = true
    this.onStatus(VAN_STATUS.THINKING)

    this.vanStartedSpeaking = false
    this.silentPolls = 0
    clearTimeout(this.responseTimer)
    this.responseTimer = setTimeout(() => this.finishTurn(), RESPONSE_TIMEOUT_MS)

    clearTimeout(this.flushTimer)
    this.flushTimer = setTimeout(() => this.send({ type: "van_speak" }), AUDIO_FLUSH_MS)
  }

  // Ready for the next question. Without this the panel strands on THINKING.
  finishTurn() {
    clearTimeout(this.responseTimer)
    this.responseTimer = null
    this.vanStartedSpeaking = false
    this.silentPolls = 0
    this.onVanSpeaking(false)
    if (!this.isClosed) this.onStatus(VAN_STATUS.CONNECTED)
  }

  // Poll VAN's inbound audio so the UI knows when it is talking and when it has
  // finished, the browser equivalent of the native client's stats poll.
  watchRemoteLevel(stream) {
    const AudioContextClass = window.AudioContext ?? window.webkitAudioContext
    if (!AudioContextClass) return

    this.audioContext = new AudioContextClass()
    const analyser = this.audioContext.createAnalyser()
    analyser.fftSize = 512
    this.audioContext.createMediaStreamSource(stream).connect(analyser)

    const samples = new Uint8Array(analyser.fftSize)
    clearInterval(this.levelTimer)
    this.levelTimer = setInterval(() => {
      analyser.getByteTimeDomainData(samples)
      let sum = 0
      for (const sample of samples) {
        const centred = (sample - 128) / 128
        sum += centred * centred
      }
      this.handleLevel(Math.sqrt(sum / samples.length))
    }, LEVEL_POLL_MS)
  }

  handleLevel(rms) {
    const isSpeaking = rms > SPEAKING_RMS
    this.onVanSpeaking(isSpeaking)

    if (isSpeaking) {
      if (!this.vanStartedSpeaking) clearTimeout(this.responseTimer)
      this.vanStartedSpeaking = true
      this.silentPolls = 0
      return
    }

    // Short gaps between sentences are not the end of the answer.
    if (!this.vanStartedSpeaking) return
    this.silentPolls += 1
    if (this.silentPolls >= SILENCE_POLLS_TO_END_TURN) this.finishTurn()
  }

  setMuted(muted) {
    if (this.micTrack) this.micTrack.enabled = !muted
  }

  send(message) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message))
  }

  fail(reason) {
    if (this.isClosed) return
    this.onStatus(VAN_STATUS.ERROR)
    this.onError(reason)
  }

  // Silences the session at once, asks the relay to save the summary job, then closes.
  // Only a session where a turn started has anything to summarise.
  close() {
    if (this.isClosed) return
    this.isClosed = true
    clearInterval(this.pingTimer)
    clearInterval(this.levelTimer)
    clearTimeout(this.flushTimer)
    clearTimeout(this.responseTimer)
    this.audioContext?.close().catch(() => {})

    this.teardownMedia()

    const sessionId = this.sessionId
    if (!sessionId || !this.hasStartedTurn || this.ws?.readyState !== WebSocket.OPEN) {
      this.closeConnections()
      return
    }
    this.endSession()
      .catch(() => false)
      .then(() => {
        this.closeConnections()
        this.onSessionEnded(sessionId)
      })
  }

  // Sends end_session and waits for session_ended; retries only on session_end_error.
  async endSession() {
    for (let attempt = 1; attempt <= MAX_END_SESSION_ATTEMPTS; attempt += 1) {
      if (this.ws?.readyState !== WebSocket.OPEN) return false
      const ack = new Promise((resolve) => {
        this.resolveEndSession = resolve
      })
      this.ws.send(JSON.stringify({ type: "end_session" }))
      const confirmed = await Promise.race([ack, wait(END_SESSION_ACK_MS).then(() => null)])
      this.resolveEndSession = null
      if (confirmed === true) return true
      if (confirmed === null) return false
      await wait(END_SESSION_RETRY_MS)
    }
    return false
  }

  // Silences VAN and releases the microphone; safe to call more than once.
  teardownMedia() {
    this.micStream?.getTracks().forEach((track) => track.stop())
    this.remoteTrack?.stop()
    if (this.remoteAudio) {
      this.remoteAudio.pause()
      this.remoteAudio.srcObject = null
    }
  }

  closeConnections() {
    this.teardownMedia()
    this.pc?.close()
    this.pc = null
    this.ws?.close()
    this.ws = null
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
